/**
 * Client-side background removal for sprite imports.
 * Given an image URL, samples the corner pixels, treats the dominant corner color
 * as "background", and produces a PNG data URL with matching pixels alpha-cut.
 * For images that already have a real alpha channel (PNGs with transparency),
 * we return the original URL untouched so we never destroy data.
 *
 * @param {string} url - image URL
 * @returns {Promise<string>} data URL (or original URL if we can't process)
 */
// Cache: every processed URL resolves once — repeat calls (stage rebuilds, editor↔play)
// are instant and flicker-free. getCleanedSync lets renderers apply the final texture
// in a SINGLE pass when the result is already known (no visible hot-swap).
const _cleanCache = new Map();

export function getCleanedSync(url) {
  const v = _cleanCache.get(url);
  return typeof v === 'string' ? v : null;
}

export async function removeBackgroundIfNeeded(url) {
  const hit = _cleanCache.get(url);
  if (hit) return hit; // string result or in-flight promise
  const p = _removeBackgroundImpl(url).catch(() => url);
  _cleanCache.set(url, p);
  const out = await p;
  _cleanCache.set(url, out);
  return out;
}

async function _removeBackgroundImpl(url) {
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('load fail'));
      el.src = url;
    });
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h || w * h > 2_500_000) return url; // too big to process safely
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return url;
    ctx.drawImage(img, 0, 0);
    let data;
    try { data = ctx.getImageData(0, 0, w, h); } catch { return url; /* CORS-tainted */ }
    const px = data.data;

    // If more than 5% of pixels already have alpha < 250, the PNG has real transparency.
    let transparent = 0;
    for (let i = 3; i < px.length; i += 4) { if (px[i] < 250) transparent++; }
    if (transparent > (px.length / 4) * 0.05) return url;

    // Sample corners + midpoints of edges to guess the background color.
    const samples = [
      [0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1],
      [Math.floor(w / 2), 0], [Math.floor(w / 2), h - 1],
      [0, Math.floor(h / 2)], [w - 1, Math.floor(h / 2)],
    ];
    let r = 0, g = 0, b = 0;
    for (const [x, y] of samples) {
      const i = (y * w + x) * 4;
      r += px[i]; g += px[i + 1]; b += px[i + 2];
    }
    r /= samples.length; g /= samples.length; b /= samples.length;

    // Only apply keying if the corners are broadly the same color (bg-like).
    let variance = 0;
    for (const [x, y] of samples) {
      const i = (y * w + x) * 4;
      variance += Math.abs(px[i] - r) + Math.abs(px[i + 1] - g) + Math.abs(px[i + 2] - b);
    }
    if (variance / samples.length > 60) return url; // corners disagree → not a solid backdrop

    // SPRITE-SHEET GUARD: if the art is an N×M grid of frames separated by background-colored
    // gutters, crop to the FIRST frame instead of rendering the raw atlas as one quad.
    const isBgLine = (vertical, pos) => {
      const len = vertical ? h : w;
      let bgCount = 0;
      for (let t = 0; t < len; t += 2) {
        const i = vertical ? ((t * w + pos) * 4) : ((pos * w + t) * 4);
        const d = Math.abs(px[i] - r) + Math.abs(px[i + 1] - g) + Math.abs(px[i + 2] - b);
        if (d < 90) bgCount++;
      }
      return bgCount / (len / 2) > 0.96;
    };
    let gridCols = 1, gridRows = 1;
    for (const n of [3, 2, 4]) {
      let ok = true;
      for (let k = 1; k < n; k++) { if (!isBgLine(true, Math.round((w * k) / n))) { ok = false; break; } }
      if (ok && gridCols === 1) gridCols = n;
      ok = true;
      for (let k = 1; k < n; k++) { if (!isBgLine(false, Math.round((h * k) / n))) { ok = false; break; } }
      if (ok && gridRows === 1) gridRows = n;
    }
    if (gridCols > 1 && gridRows > 1) {
      const cw = Math.floor(w / gridCols), ch = Math.floor(h / gridRows);
      const c2 = document.createElement('canvas');
      c2.width = cw; c2.height = ch;
      const ctx2 = c2.getContext('2d');
      ctx2.drawImage(c, 0, 0, cw, ch, 0, 0, cw, ch);
      const d2 = ctx2.getImageData(0, 0, cw, ch);
      // continue processing on the first frame only
      return keyAndVerify(c2, ctx2, d2, cw, ch, r, g, b) || url;
    }

    return keyAndVerify(c, ctx, data, w, h, r, g, b) || url;
  } catch (_) {
    return url;
  }
}

// Alpha-key the sampled background color, then verify the subject survived.
// Returns a data URL, or null when removal would destroy the subject (caller keeps original).
function keyAndVerify(c, ctx, data, w, h, r, g, b) {
  const px = data.data;
  const tolerance = 32; // gentler keying — removes only the flat backdrop, never subject shades
  for (let i = 0; i < px.length; i += 4) {
    const dr = px[i] - r, dg = px[i + 1] - g, db = px[i + 2] - b;
    const dist = Math.sqrt(dr * dr + dg * dg + db * db);
    if (dist < tolerance) px[i + 3] = 0;
    else if (dist < tolerance * 2) px[i + 3] = Math.round(((dist - tolerance) / tolerance) * 255); // wide feather kills dark halos
  }

  // STRICT VERIFICATION: the subject must survive removal. If keying erased it
  // (fully/mostly transparent result), REJECT and keep the original sprite.
  let opaque = 0;
  let minX = w, maxX = 0, minY = h, maxY = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (px[(y * w + x) * 4 + 3] > 200) {
        opaque++;
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
  }
  if (opaque < (w * h) * 0.02) return null; // subject erased → reject removal
  const bboxArea = Math.max(1, (maxX - minX + 1) * (maxY - minY + 1));
  if (opaque / bboxArea < 0.15) return null; // subject bbox mostly hollow — sparse particles inflate bbox, so keep threshold low

  ctx.putImageData(data, 0, 0);
  return c.toDataURL('image/png');
}
